import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { ToastContext } from 'cat-ecommerce-alloy';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPriceAvailability,
  proceedToCheckout
} from '@app/store/orders/actions';
import { getCookie } from '@app/utils';
import { LIST_PCC_CART_PAGE } from '@app/constants/analyticsConstants';
import { STATUS } from '@app/constants/commonConstants';
import { useAnalytics, useFitmentCheck } from '@app/hooks';
import { getGACheckoutProducts } from '@app/hooks/useAnalytics/utils';
import { updateDateAndEquipment } from '@app/store/checkout/actions';
import {
  CHECKOUT_CART_FORM_SUBMIT_BEGIN,
  CHECKOUT_CART_FORM_SUBMIT_SUCCESS,
  CHECKOUT_CART_FORM_SUBMIT_FAIL,
  SHOPPING_CART
} from '@app/store/checkout/constants';
import { setTimeSessionStorage } from '../utils';
import { CHECKOUT_INITIAL_TIME } from '../constants';
import { convertFormattedNumberToUnformatted } from '../../../utils';

const useCheckout = () => {
  const toastCtx = useContext(ToastContext);
  const { values, validateForm, setTouched, submitForm } = useFormikContext();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { fireBeginCheckoutEvent } = useAnalytics();
  const { getFitmentCheck } = useFitmentCheck();
  const current = useSelector(s => s.orders.current);
  const storeId = useSelector(s => s.common.storeId);
  const emailCart = useSelector(s => s.common.emailCart);
  const currentOrder = useSelector(s => s.orders.byId?.[current]);
  const orderTotals = useSelector(s => currentOrder?.orderTotals) ?? {};
  const orderTotal =
    convertFormattedNumberToUnformatted(orderTotals?.orderSubTotal || '') ||
    orderTotals?.unformattedOrderSubTotal;
  const dealerStoreCommon = useSelector(s => s.common.storeLocationName);
  const dealerStore =
    orderTotals?.orderExtendAttribute?.dealerStore || dealerStoreCommon;
  const isProceedToCheckoutLoading = useSelector(
    s => s.orders.isProceedToCheckoutLoading
  );
  const isProceedToCheckoutBtnDisabled = useSelector(
    s => s.orders.isProceedToCheckoutBtnDisabled
  );

  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const isRequiredFieldsLoading = useSelector(
    s => s.checkout.isRequiredFieldsLoading
  );
  const getCurrentOrderLoading = useSelector(
    s => s.orders.getCurrentOrderLoading
  );
  const cartFormSubmitStatus = useSelector(
    s => s.checkout.cartFormSubmitStatus
  );

  const getDealerCustomerStoresStatus = useSelector(
    s => s.dealerCustomer.getDealerCustomerStoresStatus
  );

  const getDealerCustomerStoresLoading =
    getDealerCustomerStoresStatus === STATUS.PENDING;
  const formSubmitProceedToCheckoutLoading =
    cartFormSubmitStatus === STATUS.PENDING;

  const isBuyOnBehalf = useSelector(s => s.common.isBuyOnBehalf);
  const isPCCCheckoutForCSR =
    useSelector(s => s.featureFlag?.PCCCheckoutForCSR) ?? true;
  const isUpdateFulfillmentDates = useSelector(
    s => s.featureFlag?.PCC_UpdateFulfillmentDates
  );
  const isCheckoutForCSRDisabled = isBuyOnBehalf && !isPCCCheckoutForCSR;

  const relatedModel = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const currentOrderItems = currentOrder?.ascendingOrderItems;

  const proceedToCheckoutSubmit = async noFormErrors => {
    setTimeSessionStorage(CHECKOUT_INITIAL_TIME);
    dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_BEGIN });
    if (noFormErrors) {
      let updateDealerSession;
      const selectedStoreId = getCookie('selESiteId');
      if (selectedStoreId !== null && selectedStoreId !== undefined) {
        if (storeId !== selectedStoreId) {
          updateDealerSession = 'Y';
        }
      }
      await dispatch(
        updateDateAndEquipment({ ...values, calledFrom: SHOPPING_CART })
      );
      isUpdateFulfillmentDates &&
        (await dispatch(fetchPriceAvailability({ invokeXorder: false })));
      const orderDetails = {
        orderId: current,
        updateDealerSession: updateDealerSession ? updateDealerSession : 'N'
      };
      dispatch(proceedToCheckout(orderDetails)).then(checkoutType => {
        if (checkoutType === 'emailCart') {
          toastCtx.destroyAllToasts();
          toastCtx.createWarningToast(t('NO_VALID_PAYMENT_EMAIL_CART'));
        }
      });
      const EQUIPMENT_VALUE = 'equipmentValue';
      sessionStorage.removeItem(EQUIPMENT_VALUE);
    }
    dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_SUCCESS });
  };

  const handleTouchedFields = async () => {
    dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_BEGIN });
    await setTouched({ equipment: true, requestByDate: true });
    const gaItems = getGACheckoutProducts({
      products: currentOrderItems.map(p => ({
        ...p,
        orderItemExtendAttribute: {
          ...p.orderItemExtendAttribute,
          googleAnalyticsAttributes: {
            ...p.orderItemExtendAttribute.googleAnalyticsAttributes,
            checkFitment: getFitmentCheck(p.partNumber),
            relatedModel
          }
        }
      })),
      emailCart
    });

    fireBeginCheckoutEvent({
      products: gaItems,
      orderTotal,
      dealerStore
    });
    const formErrors = await validateForm();
    const noFormErrors = Object.keys(formErrors).length === 0;
    if (noFormErrors) {
      setTimeout(() => {
        proceedToCheckoutSubmit(noFormErrors);
      }, 200);
    } else {
      submitForm();
      dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_FAIL });
    }
  };

  const isCheckoutButtonDisabled = useMemo(() => {
    return (
      formSubmitProceedToCheckoutLoading ||
      isProceedToCheckoutLoading ||
      isProceedToCheckoutBtnDisabled ||
      isRequiredFieldsLoading ||
      isOrderPriceAvailabilityLoading ||
      getCurrentOrderLoading ||
      getDealerCustomerStoresLoading ||
      isCheckoutForCSRDisabled
    );
  }, [
    formSubmitProceedToCheckoutLoading,
    getCurrentOrderLoading,
    getDealerCustomerStoresLoading,
    isCheckoutForCSRDisabled,
    isOrderPriceAvailabilityLoading,
    isProceedToCheckoutBtnDisabled,
    isProceedToCheckoutLoading,
    isRequiredFieldsLoading
  ]);

  return { checkoutPage: handleTouchedFields, isCheckoutButtonDisabled };
};

export default useCheckout;
